import { RefObject, useEffect, useState } from 'react'

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',')

function getFocusableElements(container: HTMLElement) {
  return Array.from(
    container.querySelectorAll<HTMLElement>(focusableSelector),
  ).filter(element => !element.closest('[hidden], [inert]'))
}

/**
 * Focus handling for a modal dialog: moves focus into the dialog when it
 * mounts (unless something inside already took it, e.g. an autofocused
 * input), keeps Tab and Shift+Tab inside it, and gives focus back to the
 * element that had it when the dialog unmounts. The dialog element needs
 * `tabIndex={-1}` so it can take focus when nothing inside can.
 */
export default function useDialogFocus(
  dialogRef: RefObject<HTMLElement | null>,
) {
  // Read while rendering, before an autofocused input in the dialog takes
  // focus on commit. Dialogs render in a portal, so only on the client
  const [returnFocusElement] = useState(() =>
    typeof document === 'undefined'
      ? null
      : (document.activeElement as HTMLElement | null),
  )

  useEffect(() => {
    const dialog = dialogRef.current

    if (!dialog) {
      return undefined
    }

    if (!dialog.contains(document.activeElement)) {
      const [firstFocusable = dialog] = getFocusableElements(dialog)

      firstFocusable.focus()
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Tab' || !dialog) {
        return
      }

      const focusable = getFocusableElements(dialog)

      if (focusable.length === 0) {
        event.preventDefault()
        return
      }

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      const active = document.activeElement

      if (event.shiftKey && (active === first || active === dialog)) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && active === last) {
        event.preventDefault()
        first.focus()
      }
    }

    dialog.addEventListener('keydown', handleKeyDown)

    return () => {
      dialog.removeEventListener('keydown', handleKeyDown)

      const active = document.activeElement
      // Leave focus alone if it already moved somewhere else on purpose
      const isFocusLost =
        !active || active === document.body || dialog.contains(active)

      if (isFocusLost && returnFocusElement?.isConnected) {
        returnFocusElement.focus()
      }
    }
  }, [dialogRef, returnFocusElement])
}
